import { useEffect, useState, useCallback, useRef } from "react";
import { FileSpreadsheet, Printer, CalendarRange, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { fmtTon } from "@/lib/utils";
import { PageHeader, Loading } from "@/components/bits";
import { MultiSelect, passes } from "@/components/MultiSelect";
import { PrintOverlay } from "@/components/printDocs";
import { LaporanPrintView } from "@/components/LaporanPrint";
import { useApp } from "@/context/AppContext";
import { VendorBarChart, TrendChart, AreaBarChart, CHART_COLORS, ChartExportButton, TrendGranularityToggle } from "@/components/charts";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList } from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

export default function Laporan() {
  const { user } = useApp();
  const [dash, setDash] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState("0");
  const [gran, setGran] = useState("month");
  const granLabel = { month: "Bulanan", week: "Mingguan", day: "Harian" }[gran];
  const monthlyRef = useRef(null), vendorRef = useRef(null), trendRef = useRef(null), areaRef = useRef(null);
  const [marks, setMarks] = useState(null);
  const [fVendor, setFVendor] = useState([]);
  const [fProyek, setFProyek] = useState([]);
  const [fArea, setFArea] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [printOpen, setPrintOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/markings?limit=2000").then((r) => setMarks(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/dashboard?year=${year}&month=${month}&granularity=${gran}`).then((r) => setDash(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [year, month, gran]);
  const periodLabel = month === "0" ? `Tahun ${year}` : `${MONTHS[Number(month) - 1]} ${year}`;
  const fileSuffix = month === "0" ? year : `${year}-${String(month).padStart(2, "0")}`;

  const rows = (marks || []).filter((m) => passes(fVendor, m.vendorId) && passes(fProyek, m.projectId) && passes(fArea, m.area));
  const vendorNames = new Set(vendors.filter((v) => fVendor.includes(v.id)).map((v) => v.nama));
  const vendorRows = (dash?.vendorBar || []).filter((v) => fVendor.length === 0 || vendorNames.has(v.vendor));
  const trend = dash?.trend || [];
  const filterActive = fVendor.length || fProyek.length || fArea.length;
  const filterLabel = [
    fProyek.length ? `Proyek: ${fProyek.map((id) => projects.find((p) => p.id === id)?.kode || id).join(", ")}` : "",
    fVendor.length ? `Vendor: ${fVendor.map((id) => { const v = vendors.find((x) => x.id === id); return v?.singkatan || v?.nama || id; }).join(", ")}` : "",
    fArea.length ? `Area: ${fArea.join(", ")}` : "",
  ].filter(Boolean).join(" · ");

  const exportCsv = () => {
    const out = [`LAPORAN PRODUKSI — Periode: ${periodLabel}`, ""];
    out.push(`REKAP TONASE STB PER VENDOR (${periodLabel})`, "Singkatan,Nama Vendor,STB (Ton)");
    vendorRows.forEach((v) => out.push([v.label, `"${v.vendor}"`, v.stb].join(",")));
    out.push("", `REKAP TONASE STB PER AREA (${periodLabel})`, "Area,STB (Ton)");
    (dash?.areaBar || []).forEach((a) => out.push([a.area, a.value].join(",")));
    out.push("", `TONASE STB ${granLabel.toUpperCase()} (${periodLabel})`, "Periode,STB (Ton),Target (Ton),Kumulatif (Ton)");
    trend.forEach((t) => out.push([t.date ? `${t.month} (${t.date})` : t.month, t.stb, t.target, t.kumulatif].join(",")));
    out.push("", "DETAIL MARKING", "Kode Marking,SPK,Proyek,Vendor,Area,Qty,QC OK,STB,Delivery,Progress %");
    rows.forEach((m) => out.push([m.code, m.spkNo, `"${m.projectName}"`, m.vendorName, m.area, m.qty, m.qcOk, m.stb, m.delivery, m.progress].join(",")));
    const blob = new Blob(["\uFEFF" + out.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `laporan-produksi-${fileSuffix}.csv`;
    a.click();
    toast.success("Laporan diexport / 报表已导出");
  };

  return (
    <div className="space-y-5">
      <PageHeader id="Laporan Produksi & Export" zh="生产报告与导出" desc="Rekap produksi per periode, vendor & proyek — siap export."
        actions={<>
          <Button onClick={exportCsv} variant="outline" className="border-slate-700" data-testid="laporan-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" />Export Excel/CSV</Button>
          <Button onClick={() => setPrintOpen(true)} variant="outline" className="border-slate-700" disabled={!dash} data-testid="laporan-print-button"><Printer className="mr-2 h-4 w-4" />Export PDF</Button>
        </>} />

      <div className="panel flex flex-wrap items-center gap-2 p-3" data-testid="laporan-report">
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-400"><CalendarRange className="h-3.5 w-3.5 text-sky-400" />Periode</span>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="h-8 w-24 border-slate-800 bg-slate-900/70 text-xs" data-testid="laporan-year-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0D1524] border-slate-800">
            {(dash?.filter?.years || [Number(year)]).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-8 w-32 border-slate-800 bg-slate-900/70 text-xs" data-testid="laporan-month-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0D1524] border-slate-800">
            <SelectItem value="0">Semua Bulan</SelectItem>
            {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="h-5 w-px bg-slate-800" />
        <MultiSelect testid="laporan-filter-project" placeholder="Semua Proyek" width="w-48" menuWidth="w-80" options={projects.map((p) => ({ value: p.id, label: p.kode, sub: p.nama }))} value={fProyek} onChange={setFProyek} />
        <MultiSelect testid="laporan-filter-vendor" placeholder="Semua Vendor" width="w-44" options={vendors.map((v) => ({ value: v.id, label: v.singkatan || v.nama, sub: v.singkatan ? v.nama : undefined }))} value={fVendor} onChange={setFVendor} />
        <MultiSelect testid="laporan-filter-area" placeholder="Semua Area" width="w-32" menuWidth="w-48" options={[...new Set((marks || []).map((m) => m.area).filter(Boolean))].sort().map((a) => ({ value: a, label: a }))} value={fArea} onChange={setFArea} />
        {filterActive ? <button type="button" onClick={() => { setFProyek([]); setFVendor([]); setFArea([]); }} title="Reset filter" className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200" data-testid="laporan-filter-reset"><X className="h-3.5 w-3.5" /></button> : null}
        <span className="ml-auto self-center font-mono text-xs text-slate-500" data-testid="laporan-period">{dash ? `${periodLabel} · STB ${dash.stbInputTotal} Ton · ${rows.length} marking` : ""}</span>
      </div>

      {!dash ? <Loading /> : (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="panel p-4">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="font-display text-sm font-bold text-slate-200">Tonase STB {granLabel} (Ton) <span className="cn text-[11px] font-normal text-slate-500">交接吨位</span></h3>
              <ChartExportButton targetRef={monthlyRef} filename={`stb-bulanan-${year}`} title={`Tonase STB Bulanan · Tahun ${year}`} testid="laporan-export-monthly-chart" />
            </div>
            <div ref={monthlyRef}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={trend} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={{ stroke: "#334155" }} tickLine={false} />
                <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={() => null} cursor={{ fill: "rgba(30,41,59,.4)" }} />
                <Legend wrapperStyle={{ color: "#94A3B8", fontSize: 11 }} />
                <Bar dataKey="stb" name="STB (Ton)" fill={CHART_COLORS.stb} maxBarSize={26}>
                  <LabelList dataKey="stb" position="top" fill="#CBD5E1" fontSize={10} formatter={(v) => (Number(v) ? Number(v).toLocaleString("id-ID", { maximumFractionDigits: 1 }) : "")} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </div>
          </div>
          <div className="panel p-4">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="font-display text-sm font-bold text-slate-200">Tonase STB per Vendor <span className="cn text-[11px] font-normal text-slate-500">供应商交接吨位</span></h3>
              <ChartExportButton targetRef={vendorRef} filename={`stb-vendor-${fileSuffix}`} title={`Tonase STB per Vendor · ${periodLabel}`} testid="laporan-export-vendor-chart" />
            </div>
            <div ref={vendorRef}><VendorBarChart data={vendorRows} /></div>
          </div>
          <div className="panel p-4 lg:col-span-2">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="font-display text-sm font-bold text-slate-200">Trend STB Kumulatif vs Target <span className="cn text-[11px] font-normal text-slate-500">交接累计趋势</span></h3>
              <span className="inline-flex items-center gap-1.5">
                <TrendGranularityToggle value={gran} onChange={setGran} testid="laporan-trend-granularity" />
                <ChartExportButton targetRef={trendRef} filename={`trend-stb-${gran}-${fileSuffix}`} title={`Trend STB · ${periodLabel}`} testid="laporan-export-trend-chart" />
              </span>
            </div>
            <div ref={trendRef}><TrendChart data={trend} granularity={gran} /></div>
          </div>
          <div className="panel p-4 lg:col-span-2" data-testid="laporan-area-panel">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="font-display text-sm font-bold text-slate-200">Tonase STB per Area <span className="cn text-[11px] font-normal text-slate-500">区域交接吨位</span></h3>
              <span className="inline-flex items-center gap-2">
                <span className="font-mono text-[11px] text-slate-500" data-testid="laporan-area-total">{(dash?.areaBar || []).length} area · {fmtTon((dash?.areaBar || []).reduce((a, x) => a + (x.value || 0), 0))} T</span>
                <ChartExportButton targetRef={areaRef} filename={`stb-area-${fileSuffix}`} title={`Tonase STB per Area · ${periodLabel}`} testid="laporan-export-area-chart" />
              </span>
            </div>
            <div ref={areaRef}><AreaBarChart data={dash?.areaBar || []} /></div>
          </div>
        </div>
      )}

      {printOpen && dash && (
        <PrintOverlay testid="laporan-print-overlay" onClose={() => setPrintOpen(false)}>
          <LaporanPrintView dash={dash} periodLabel={periodLabel} granLabel={granLabel} vendorRows={vendorRows} rows={rows} filterLabel={filterLabel} user={user} />
        </PrintOverlay>
      )}
    </div>
  );
}